import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType, provideEffects } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { debounceTime, map, pairwise, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import { DateRangeValueType } from '@core/date-range-types';
import { createEffectFactory } from '@core/ngrx-legacy';
import {
  ReportsComponentsActions,
  ReportsDashboardsActions,
  ReportsDashboardsSelectors,
} from '@core/reports-data';
import {
  ReportDashboardFilters,
  ReportDateFilter,
  ReportParamFilters,
  ReportDashboard,
} from '@core/reports-types';
import { getDaysCountInDateRange } from '@core/reports-util';

import { ReportsAnalytics } from '../analytics/report-dashboards-analytics.service';

@Injectable({ providedIn: 'root' })
export class ReportsUserTrackingEffects {
  private readonly actions$ = inject(Actions);
  private readonly analytics = inject(ReportsAnalytics);
  private readonly store = inject(Store);

  public effects = createEffectFactory({}, this.actions$);

  public trackDashboardViewed$ = this.store
    .pipe(
      select(ReportsDashboardsSelectors.activeWithReferences),
      filterBoolean,
      debounceTime(3000),
    )
    .subscribe(dashboard => {
      this.analytics.dashboardViewed({
        id: dashboard.id,
        origin: dashboard.origin ?? 'blank',
        templateId: dashboard.templateId ?? null,
        name: dashboard.name,
        visibility: dashboard.visibility.level,
        componentsTemplateIds: dashboard.components
          .map(component => component.templateId)
          .filter((x): x is string => x !== null),
        componentsMetrics: dashboard.components.map(x => x.metric),
        generated: dashboard.generated,
      });
    });

  public trackDashboardCreated$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ReportsDashboardsActions.createOneOptimisticSuccess,
          ReportsDashboardsActions.duplicateOneSuccess,
        ),
        tap(({ payload }) =>
          this.analytics.dashboardCreated({
            origin: payload.data.origin ?? 'blank',
            visibility: payload.data.visibility.level,
            templateId: payload.data.templateId ?? null,
          }),
        ),
      ),
    { dispatch: false },
  );

  public trackDashboardDeleted$ = this.effects(
    ReportsDashboardsActions.removeOneOptimisticSuccess,
    ({ payload }) =>
      this.analytics.dashboardDeleted({
        origin: payload.data.origin ?? 'blank',
        templateId: payload.data.templateId ?? null,
        visibility: payload.data?.visibility?.level,
        generated: payload.data.generated,
      }),
    { dispatch: false },
  );

  public dashboardPrevFilters$ = this.store.pipe(
    select(ReportsDashboardsSelectors.activeFilters),
    pairwise(),
    map(([prev]) => prev),
  );

  public trackCustomDashboardFilterChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportsDashboardsActions.updateFiltersOptimistic),
        switchMap(({ payload }) =>
          this.store.select(ReportsDashboardsSelectors.byId(payload.data.id)).pipe(
            filterBoolean,
            take(1),
            map(dashboard => ({
              ...payload,
              data: {
                ...payload.data,
                origin: dashboard.origin,
                templateId: dashboard.templateId,
                generated: dashboard.generated,
              },
            })),
          ),
        ),
        withLatestFrom(this.dashboardPrevFilters$),
        tap(([payload, dashboardFilters]) =>
          this.handleDashboardFilterChange(payload, dashboardFilters),
        ),
      ),
    { dispatch: false },
  );

  public trackComponentCreated$ = this.effects(
    ReportsComponentsActions.createOneOptimisticSuccess,
    ({ payload }) => this.analytics.dashboardComponentCreated(payload.data, payload.data.origin),
    { dispatch: false },
  );

  public trackComponentDuplicated$ = this.effects(
    ReportsComponentsActions.duplicateComponentSuccess,
    ({ payload }) => this.analytics.dashboardComponentCreated(payload.data, 'duplicate'),
    { dispatch: false },
  );

  public trackComponentRemoved$ = this.effects(
    ReportsComponentsActions.removeOneOptimisticSuccess,
    ({ payload }) => this.analytics.dashboardComponentDeleted(payload.data, payload.data.origin),
    { dispatch: false },
  );

  public trackComponentUpdated$ = this.effects(
    ReportsComponentsActions.updateOneOptimisticSuccess,
    ({ payload }) => this.analytics.dashboardComponentUpdated(payload.data, payload.data.origin),
    { dispatch: false },
  );

  public trackDashboardLayoutChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportsComponentsActions.updateGridPropsOptimistic),
        concatLatestFrom(({ payload }) =>
          this.store.select(ReportsDashboardsSelectors.byId(payload.data.dashboardId)),
        ),
        map(([, dashboard]) => dashboard!),
        tap(dashboard =>
          this.analytics.dashboardLayoutChanged({
            id: dashboard.id,
            origin: dashboard.origin ?? 'blank',
            templateId: dashboard.templateId ?? null,
            generated: dashboard.generated,
          }),
        ),
      ),
    { dispatch: false },
  );

  private handleDashboardFilterChange(
    payload: {
      kind: 'filters' | 'date' | 'reset';
      data: {
        id: number | string;
        filters: ReportParamFilters;
        dateFilter: ReportDateFilter;
        origin: ReportDashboard['origin'];
        templateId: ReportDashboard['templateId'];
        generated: boolean;
      };
    },
    { filters }: ReportDashboardFilters,
  ) {
    const templateId = payload.data.templateId ?? null;
    const origin = payload.data.origin ?? 'blank';
    // eslint-disable-next-line default-case
    switch (payload.kind) {
      case 'filters':
        const prev = Array.from(new Set(filters.map(f => f.group)));
        const next = Array.from(new Set(payload.data.filters.map(f => f.group)));

        const added = next.find(group => !prev.includes(group));
        const removed = prev.find(group => !next.includes(group));

        if (added) {
          this.analytics.dashboardFilterAddGroup({
            id: payload.data.id,
            group: added,
            templateId,
            origin,
            generated: payload.data.generated,
          });
        } else if (removed) {
          this.analytics.dashboardFilterRemoveGroup({
            id: payload.data.id,
            group: removed,
            templateId,
            origin,
            generated: payload.data.generated,
          });
        }

        break;

      case 'date':
        this.analytics.dashboardFilterSetDate({
          id: payload.data.id,
          dateRange: getDaysCountInDateRange(
            payload.data.dateFilter.dateRange,
            payload.data.dateFilter.dateStart,
            payload.data.dateFilter.dateEnd,
          ),
          dateRangeType: <DateRangeValueType>payload.data.dateFilter.dateRange,
          templateId,
          origin,
          generated: payload.data.generated,
        });

        break;
      case 'reset':
        this.analytics.dashboardFilterReset({
          id: payload.data.id,
          templateId,
          origin,
          generated: payload.data.generated,
        });
        break;
    }
  }
}

export const provideReportsUserTracking = () => provideEffects([ReportsUserTrackingEffects]);
